import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import PageTitle from '../components/PageTitle';
import ReCAPTCHA from 'react-google-recaptcha';
import emailjs from '@emailjs/browser';
import { 
  MessageSquare, 
  Mail, 
  Upload,
  Send,
  CheckCircle2,
  AlertCircle,
  ArrowLeft,
  Bug,
  Lightbulb,
  Shield
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardHeader, CardContent, CardDescription, CardTitle } from '../components/ui/card';
import { Alert } from '../components/ui/alert';
import { Badge } from '../components/ui/badge';

const Contato = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    type: 'bug', // bug, suggestion, other
    description: '',
    attachment: null
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [captchaValue, setCaptchaValue] = useState(null);
  
  const recaptchaRef = useRef();
  const fileInputRef = useRef();

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file && file.size > 5 * 1024 * 1024) { // 5MB limit
      setError('Arquivo deve ter no máximo 5MB');
      return;
    }
    setFormData(prev => ({
      ...prev,
      attachment: file
    }));
  };

  const handleCaptchaChange = (value) => {
    setCaptchaValue(value);
    setError('');
  };

  const validateForm = () => {
    if (!formData.name.trim()) {
      setError('Nome é obrigatório');
      return false;
    }
    
    if (!formData.email.trim() || !/\S+@\S+\.\S+/.test(formData.email)) {
      setError('Email válido é obrigatório');
      return false;
    }
    
    if (!formData.description.trim()) {
      setError('Descrição é obrigatória');
      return false;
    }
    
    if (!captchaValue) {
      setError('Por favor, complete a verificação reCAPTCHA');
      return false;
    }
    
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);
    
    try {
      // Prepare email template parameters
      const templateParams = {
        from_name: formData.name,
        from_email: formData.email,
        message_type: formData.type === 'bug' ? 'Relatório de Bug' : 
                     formData.type === 'suggestion' ? 'Sugestão de Melhoria' : 'Outro',
        message: formData.description,
        attachment_info: formData.attachment ? 
          `Arquivo anexado: ${formData.attachment.name} (${(formData.attachment.size / 1024).toFixed(2)}KB)` : 
          'Nenhum arquivo anexado',
        to_email: 'brunorodrigotubio@gmail.com'
      };

      // Send email using EmailJS
      const result = await emailjs.send(
        'service_v6p9niy', 
        'template_n9kcw1q', 
        templateParams,
        'p7fb_1dlS0N9Gc4wT'
      );

      if (result.status === 200) {
        setSubmitted(true);
        setFormData({
          name: '',
          email: '',
          type: 'bug',
          description: '',
          attachment: null
        });
        recaptchaRef.current?.reset();
        setCaptchaValue(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }
    } catch (error) {
      console.error('Erro ao enviar email:', error);
      setError('Erro ao enviar mensagem. Tente novamente ou entre em contato diretamente: brunorodrigotubio@gmail.com');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <>
        <PageTitle 
          title="Mensagem Enviada" 
          description="Sua mensagem foi enviada com sucesso"
        />
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background to-muted/20 p-4">
        <Card className="w-full max-w-md shadow-lg">
          <CardHeader className="text-center">
            <div className="w-16 h-16 bg-church-green/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="h-8 w-8 text-church-green" />
            </div>
            <CardTitle className="text-xl text-church-blue">Mensagem Enviada!</CardTitle>
            <CardDescription>
              Obrigado pelo seu feedback. Retornaremos em breve.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground text-center">
              Sua mensagem foi enviada com sucesso para nossa equipe. 
              Analisaremos seu feedback e entraremos em contato se necessário.
            </p>
            <div className="flex flex-col gap-2">
              <Button asChild className="w-full">
                <Link to="/">
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Voltar ao Início
                </Link>
              </Button>
              <Button variant="ghost" onClick={() => setSubmitted(false)} className="w-full">
                Enviar Outra Mensagem
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
      </>
    );
  }

  return (
    <>
      <PageTitle 
        title="Contato" 
        description="Entre em contato com a equipe da Igreja de Jardim"
      />
      <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <section className="igreja-hero text-white py-20 lg:py-32">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center space-y-8">
            <Badge className="bg-white/20 text-white border-white/30 mb-4">
              Suporte & Contato
            </Badge>
            <h1 className="text-4xl lg:text-6xl font-bold leading-tight">
              Fale
              <span className="block text-church-green">Conosco</span>
            </h1>
            <p className="text-xl lg:text-2xl text-white/90 leading-relaxed max-w-3xl mx-auto">
              Encontrou um problema ou tem uma sugestão? Estamos aqui para ajudar 
              a tornar sua experiência ainda melhor.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="grid lg:grid-cols-3 gap-12">
              {/* Contact Types */}
              <div className="space-y-6">
                <h2 className="text-2xl font-bold text-church-blue mb-6">
                  Como podemos ajudar?
                </h2>
                
                <Card className="hover:shadow-lg transition-all duration-300">
                  <CardHeader>
                    <div className="w-12 h-12 bg-red-500/10 rounded-lg flex items-center justify-center mb-4">
                      <Bug className="h-6 w-6 text-red-600" />
                    </div>
                    <CardTitle className="text-lg">Reportar Bug</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground text-sm">
                      Encontrou algo que não está funcionando? Relate o problema para que possamos corrigi-lo.
                    </p>
                  </CardContent>
                </Card>

                <Card className="hover:shadow-lg transition-all duration-300">
                  <CardHeader>
                    <div className="w-12 h-12 bg-blue-500/10 rounded-lg flex items-center justify-center mb-4">
                      <Lightbulb className="h-6 w-6 text-blue-600" />
                    </div>
                    <CardTitle className="text-lg">Sugestão</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground text-sm">
                      Tem uma ideia para melhorar a plataforma? Compartilhe sua sugestão conosco.
                    </p>
                  </CardContent>
                </Card>

                <Card className="hover:shadow-lg transition-all duration-300">
                  <CardHeader>
                    <div className="w-12 h-12 bg-green-500/10 rounded-lg flex items-center justify-center mb-4">
                      <MessageSquare className="h-6 w-6 text-green-600" />
                    </div>
                    <CardTitle className="text-lg">Contato Direto</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground text-sm mb-3">
                      Para questões urgentes ou feedback geral.
                    </p>
                    <a 
                      href="mailto:brunorodrigotubio@gmail.com" 
                      className="text-church-blue hover:underline text-sm flex items-center gap-2"
                    >
                      <Mail className="h-4 w-4" />
                      brunorodrigotubio@gmail.com
                    </a>
                  </CardContent>
                </Card>
              </div>

              {/* Contact Form */}
              <div className="lg:col-span-2">
                <Card className="shadow-lg">
                  <CardHeader>
                    <CardTitle className="text-2xl text-church-blue">Enviar Mensagem</CardTitle>
                    <CardDescription>
                      Preencha o formulário abaixo e retornaremos em breve
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {error && (
                      <Alert variant="destructive" className="mb-6">
                        <AlertCircle className="h-4 w-4" />
                        {error}
                      </Alert>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-6">
                      {/* Name */}
                      <div className="space-y-2">
                        <Label htmlFor="name">Nome *</Label>
                        <Input
                          id="name"
                          name="name"
                          type="text"
                          value={formData.name}
                          onChange={handleInputChange}
                          placeholder="Seu nome completo"
                          disabled={loading}
                          required
                        />
                      </div>

                      {/* Email */}
                      <div className="space-y-2">
                        <Label htmlFor="email">Email *</Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          placeholder="seu@email.com"
                          disabled={loading}
                          required
                        />
                      </div>

                      {/* Type */}
                      <div className="space-y-2">
                        <Label htmlFor="type">Tipo de Contato *</Label>
                        <select
                          id="type"
                          name="type"
                          value={formData.type}
                          onChange={handleInputChange}
                          disabled={loading}
                          className="w-full px-3 py-2 border border-input rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-ring"
                        >
                          <option value="bug">🐛 Reportar Bug</option>
                          <option value="suggestion">💡 Sugestão de Melhoria</option>
                          <option value="other">💬 Outro</option>
                        </select>
                      </div>

                      {/* Description */}
                      <div className="space-y-2">
                        <Label htmlFor="description">Descrição *</Label>
                        <Textarea
                          id="description"
                          name="description"
                          value={formData.description}
                          onChange={handleInputChange}
                          placeholder={
                            formData.type === 'bug' 
                              ? "Descreva o problema: O que aconteceu? Quando aconteceu? Qual era o comportamento esperado?"
                              : formData.type === 'suggestion'
                              ? "Descreva sua sugestão: Que funcionalidade gostaria de ver? Como isso melhoraria sua experiência?"
                              : "Descreva sua mensagem..."
                          }
                          disabled={loading}
                          rows={6}
                          required
                        />
                      </div>

                      {/* File Attachment */}
                      <div className="space-y-2">
                        <Label htmlFor="attachment">Anexo (opcional)</Label>
                        <div className="flex items-center gap-4">
                          <Input
                            ref={fileInputRef}
                            id="attachment"
                            type="file"
                            onChange={handleFileChange}
                            disabled={loading}
                            accept=".jpg,.jpeg,.png,.gif,.pdf,.doc,.docx,.txt"
                            className="file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-church-blue file:text-white hover:file:bg-church-blue/90"
                          />
                        </div>
                        {formData.attachment && (
                          <p className="text-sm text-muted-foreground flex items-center gap-2">
                            <Upload className="h-4 w-4" />
                            {formData.attachment.name} ({(formData.attachment.size / 1024).toFixed(2)}KB)
                          </p>
                        )}
                        <p className="text-xs text-muted-foreground">
                          Formatos aceitos: JPG, PNG, GIF, PDF, DOC, DOCX, TXT. Máximo 5MB.
                        </p>
                      </div>

                      {/* reCAPTCHA */}
                      <div className="space-y-2">
                        <Label>Verificação de Segurança *</Label>
                        <ReCAPTCHA
                          ref={recaptchaRef}
                          sitekey="6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI" // Test key - replace with your actual key
                          onChange={handleCaptchaChange}
                        />
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          <Shield className="h-3 w-3" />
                          <span>Protegido por reCAPTCHA para evitar spam</span>
                        </div>
                      </div>

                      {/* Submit Button */}
                      <Button 
                        type="submit" 
                        disabled={loading || !captchaValue} 
                        className="w-full"
                        size="lg"
                        variant="igreja"
                      >
                        {loading ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                            Enviando...
                          </>
                        ) : (
                          <>
                            <Send className="h-4 w-4 mr-2" />
                            Enviar Mensagem
                          </>
                        )}
                      </Button>
                    </form>

                    <div className="mt-6 pt-6 border-t border-border">
                      <p className="text-sm text-muted-foreground text-center">
                        Ao enviar esta mensagem, você concorda que podemos entrar em contato 
                        através do email fornecido para responder sua solicitação.
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="sugestoes" className="py-20 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-church-blue mb-4">
                Dúvidas Frequentes
              </h2>
              <p className="text-muted-foreground">
                Respostas para as perguntas mais comuns
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Como reportar um bug?</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground text-sm">
                    Descreva detalhadamente o problema: o que você estava fazendo, 
                    o que aconteceu e o que deveria ter acontecido. Anexe capturas 
                    de tela se possível.
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Quanto tempo leva para responder?</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground text-sm">
                    Tentamos responder todas as mensagens em até 48 horas. 
                    Bugs críticos têm prioridade e são analisados mais rapidamente.
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Posso sugerir novas funcionalidades?</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground text-sm">
                    Sim! Adoramos receber sugestões. Explique como a funcionalidade 
                    funcionaria e como ela melhoraria sua experiência na plataforma.
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Meus dados estão seguros?</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground text-sm">
                    Sim, usamos reCAPTCHA para proteger contra spam e seus dados 
                    são enviados de forma segura. Não compartilhamos suas informações 
                    com terceiros.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </div>
    </>
  );
};

export default Contato;